import {
    Activity,
    Antenna,
    Briefcase,
    BarChart3,
    Building2,
    Euro,
    Flame,
    GraduationCap,
    Grid2x2,
    HeartHandshake,
    HeartPulse,
    Home,
    Landmark,
    Leaf,
    Map as MapIcon,
    Recycle,
    Route,
    Ruler,
    Spline,
    Thermometer,
    Mountain,
    TreePine,
    Shield,
    ShoppingBag,
    TrainFront,
    Vote,
    Users,
    Zap,
} from 'lucide-react';
import { PoIGroup } from '@the-real-insight/tri-model';

/**
 * The mark in front of a group in the metric list.
 *
 * A list of forty-odd groups with nothing but chevrons down its left edge reads
 * as one undifferentiated column: the eye has to fall into the words to find
 * anything. A symbol and a colour give each group a shape that can be aimed at,
 * and the colour carries the subject — money green, heating orange, people blue —
 * so that neighbouring entries about entirely different things stop looking
 * alike.
 *
 * Where a colour already means something on the map, it is that colour: the
 * cadastre's ochre, the grid's blue, the strong quake's red. A legend and a list
 * that disagree about what blue means would be worse than no colour at all.
 */

/** Subject families, each with the colour it is drawn in. */
const FAMILIES = {
    people: { color: '#4C6EDB', icon: Users },
    household: { color: '#7A5CC4', icon: Home },
    building: { color: '#B4762A', icon: Building2 },
    money: { color: '#1E8E5A', icon: Euro },
    heating: { color: '#D2601A', icon: Flame },
    space: { color: '#5B7A8C', icon: Ruler },
    other: { color: '#7A8794', icon: BarChart3 },
} as const;

type Family = keyof typeof FAMILIES;

/**
 * Which family a Zensus theme belongs to, read off its key.
 *
 * The catalogue is loaded from an import and grows on its own — a table of the
 * forty-odd keys would be a list to forget to extend, and a new theme would show
 * up unmarked. The keys are slugs of their German names, so the subject is in the
 * words: anything about rent is about money, anything with `gebaeude` in it is
 * about buildings.
 *
 * Order matters. "Größe des privaten Haushalts" contains both `groesse` and
 * `haushalt`, and it is a household theme — so the household test comes before
 * the one for areas.
 */
const FAMILY_RULES: [Family, RegExp][] = [
    ['money', /miete|eigentuemer|leerstand|kaufwert/],
    ['heating', /heizung|energietraeger|brennstoff/],
    ['household', /haushalt|familie|lebensform|kernfamilie|senioren/],
    ['building', /gebaeude|wohnung|wohnraum|baujahr|gebaeudetyp/],
    [
        'people',
        /alter|jaehrige|bevoelkerung|staatsangehoerig|auslaender|deutsche|geburtsland|religion|familienstand|geschlecht/,
    ],
    ['space', /flaeche|raeume|groesse|zimmer/],
];

const familyOfTheme = (theme: string): Family =>
    FAMILY_RULES.find(([, pattern]) => pattern.test(theme))?.[0] ?? 'other';

/**
 * The fixed groups — the ones this page states rather than loads.
 *
 * Their colours are taken from what the map already draws: the parcels' ochre,
 * the grid infrastructure's blue, the colour of a magnitude 6.
 */
const FIXED: Record<string, { color: string; icon: any }> = {
    layers: { color: '#B08A32', icon: Grid2x2 },
    power: { color: '#3388FF', icon: Zap },
    hazards: { color: '#E2571E', icon: Activity },
    climate: { color: '#D7191C', icon: Thermometer },
    nature: { color: '#2E7D32', icon: TreePine },
    planning: { color: '#3949AB', icon: Landmark },
    ways: { color: '#D2601A', icon: Route },
    bridges: { color: '#8A6D3B', icon: Spline },
    [`poi:${PoIGroup.infrastructure}`]: { color: '#5C6BC0', icon: Antenna },
    [`poi:${PoIGroup.mobility}`]: { color: '#0E7C86', icon: TrainFront },
    [`poi:${PoIGroup.education}`]: { color: '#2E6FBF', icon: GraduationCap },
    [`poi:${PoIGroup.health}`]: { color: '#C2185B', icon: HeartPulse },
    [`poi:${PoIGroup.safety}`]: { color: '#B23A2E', icon: Shield },
    [`poi:${PoIGroup.commerce}`]: { color: '#8E6BBF', icon: ShoppingBag },
    [`poi:${PoIGroup.environment}`]: { color: '#2E7D4F', icon: Leaf },
};

/**
 * The INKAR groups, which are entity types of the model rather than something
 * this page names.
 *
 * Keyed by `inkar:<Type>` — the same id the list opens them with. Their colours
 * follow the families above, so an indicator about people looks like a Zensus
 * theme about people: the two are different sources for the same subject, and a
 * reader comparing them should not have to translate colours in their head.
 */
const INKAR_ICONS: Record<string, { color: string; icon: any }> = {
    PopulationData: FAMILIES.people,
    EconomicsData: FAMILIES.money,
    IncomeData: { color: FAMILIES.money.color, icon: Euro },
    EmploymentData: { color: '#2F6F8F', icon: Briefcase },
    BuildingAndHousingData: FAMILIES.building,
    LandUseData: { color: '#6E8B3D', icon: MapIcon },
    TrafficData: { color: '#0E7C86', icon: TrainFront },
    CrimeStatistics: { color: '#B23A2E', icon: Shield },
    PublicFinancesData: { color: FAMILIES.money.color, icon: Landmark },
    SocialSupplyData: { color: '#7A5CC4', icon: HeartHandshake },
    SocialBenefitsData: { color: '#7A5CC4', icon: HeartHandshake },
    SubsidiesData: { color: '#7A5CC4', icon: HeartHandshake },
    PoliticsData: { color: '#5C6BC0', icon: Vote },
    EnvironmentData: { color: '#2E7D4F', icon: Recycle },
    SubjectOfStudy: { color: '#2E6FBF', icon: GraduationCap },
    StudentsNationalityGender: { color: '#2E6FBF', icon: GraduationCap },
};

/**
 * The icon for a group, by the same id the list opens and closes it with.
 *
 * `theme:<key>` for a Zensus theme, `poi:<group>` for a kind of place, and the
 * fixed ids for the rest. An id nobody has an opinion about still gets a mark
 * rather than nothing, because a single unmarked row in a marked column looks
 * like a fault.
 */
export const groupIcon = (id: string, size = 14) => {
    const inkar = id.startsWith('inkar:') ? INKAR_ICONS[id.slice('inkar:'.length)] : undefined;
    const fixed = inkar ?? FIXED[id];
    const { color, icon: Icon } = fixed ?? FAMILIES[familyOfTheme(id.replace(/^theme:/, ''))];

    return <Icon size={size} strokeWidth={2} color={color} />;
};

/** Die inhaltlichen Kategorien (categories.ts) — dieselben Farben wie die Gruppen, die sie zuerst enthalten. */
const CATEGORY_ICONS: Record<string, { color: string; icon: any }> = {
    planning: { color: '#3949AB', icon: Landmark },
    housing: FAMILIES.building,
    population: FAMILIES.people,
    economy: FAMILIES.money,
    mobility: { color: '#0E7C86', icon: TrainFront },
    social: { color: '#7A5CC4', icon: HeartHandshake },
    environment: { color: '#2E7D32', icon: Mountain },
    safety: { color: '#B23A2E', icon: Shield },
    other: FAMILIES.other,
};

export const categoryIcon = (key: string, size = 14) => {
    const { color, icon: Icon } = CATEGORY_ICONS[key] ?? FAMILIES.other;
    return <Icon size={size} strokeWidth={2} color={color} />;
};
